import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { Button, Card, CardContent } from '@roger/ui-components';
import type { WoundResponse } from '../../services/api';
import AddWoundButton from './AddWoundButton';
import WoundsTable from './WoundsTable';
import WoundCareWorksheetModal from './WoundCareWorksheetModal';

interface WoundsCardProps {
  patientId: string;
  wounds: WoundResponse[];
  isLoading: boolean;
  error: string | null;
  canAdd?: boolean;
  canEdit?: boolean;
  visitId?: string;
  onDocumentCreated?: () => void;
  onRetry: () => void;
  onAddWound: () => void;
  onEditWound: (wound: WoundResponse) => void;
  onDeleteWound: (wound: WoundResponse) => void;
  onDiscontinueWound: (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
  onOpenAssessments: (wound: WoundResponse) => void;
}

const WoundsCard: React.FC<WoundsCardProps> = ({
  patientId,
  wounds,
  isLoading,
  error,
  canAdd = false,
  canEdit = true,
  visitId,
  onDocumentCreated,
  onRetry,
  onAddWound,
  onEditWound,
  onDeleteWound,
  onDiscontinueWound,
  onOpenAssessments,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Card>
        <CardContent className='pt-4'>
          <div className='mb-3 flex items-center justify-end gap-2'>
            {visitId && (
              <Button
                size='sm'
                variant='outline'
                onClick={() => setIsModalOpen(true)}
                disabled={isLoading}
              >
                <Download className='h-4 w-4 mr-1' />
                Export
              </Button>
            )}
            <AddWoundButton
              onClick={onAddWound}
              disabled={!canAdd}
              disabledReason={
                !canEdit
                  ? 'Cannot edit because the visit is not in an editable state'
                  : undefined
              }
            />
          </div>

          {isLoading ? (
            <div className='flex justify-center py-8'>
              <div className='animate-spin rounded-full border-b-2 border-primary h-6 w-6' />
            </div>
          ) : error ? (
            <div className='text-center py-8 space-y-2'>
              <p className='text-sm text-destructive'>{error}</p>
              <button
                onClick={onRetry}
                className='text-xs text-muted-foreground underline hover:text-foreground'
              >
                Try again
              </button>
            </div>
          ) : wounds.length === 0 ? (
            <p className='text-sm text-muted-foreground text-center py-8'>
              {canAdd
                ? 'No wounds recorded. Click "Add Wound" to document one.'
                : 'No wounds recorded for this patient.'}
            </p>
          ) : (
            <WoundsTable
              wounds={wounds}
              canEdit={canEdit}
              canDiscontinue={canAdd}
              onEdit={onEditWound}
              onDelete={onDeleteWound}
              onDiscontinue={onDiscontinueWound}
              onOpenAssessments={onOpenAssessments}
            />
          )}
        </CardContent>
      </Card>

      {visitId && (
        <WoundCareWorksheetModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          patientId={patientId}
          visitId={visitId}
          onDocumentCreated={onDocumentCreated}
        />
      )}
    </>
  );
};

export default WoundsCard;
